import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { playEndChime } from './utils/soundUtils';
import { showOSNotification } from './utils/notify';
import Home from './pages/Home';
import CV from './pages/CV';
import ResearchNew from './pages/ResearchNew';
import Visits from './pages/Visits';
import Portfolio from './pages/Portfolio';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Sandbox from './pages/Sandbox';
import Admin from './pages/Admin';
import Pomodoro from './pages/Pomodoro';
import Notes from './pages/Notes';
import Sticker from './pages/Sticker';
import Table from './pages/Table';
import Tools from './pages/Tools';
import Habit from './pages/Habit';
import Recipe from './pages/Recipe';
import Todo from './pages/Todo';

function getDefaultTheme() {
  const saved = localStorage.getItem('theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return 'light';   // 默认白天；之前是按访问时间在 7–19 点之外自动转深色
}

const SunIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <line x1="12" y1="2" x2="12" y2="5" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="2" y1="12" x2="5" y2="12" />
    <line x1="19" y1="12" x2="22" y2="12" />
    <line x1="4.6" y1="4.6" x2="6.7" y2="6.7" />
    <line x1="17.3" y1="17.3" x2="19.4" y2="19.4" />
    <line x1="4.6" y1="19.4" x2="6.7" y2="17.3" />
    <line x1="17.3" y1="6.7" x2="19.4" y2="4.6" />
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

// Per-route browser-tab title
const PAGE_TITLES = {
  '/': 'Carolinge Li',
  '/cv': 'CV',
  '/research': 'Research',
  '/portfolio': 'Portfolio',
  '/blog': 'Blog',
  '/t': 'Tools',
  '/sb': 'SBoard',
  '/ha': 'Habit',
  '/cook': '菜谱',
  '/notes': 'Notes',
  '/sticker': 'Sticker',
  '/table': 'Table',
  '/todo': 'TODO',
  '/sandbox': 'Sandbox',
  '/admin': 'Admin',
};

// Tab-title countdown while a pomodoro is running — /sb only, not site-wide.
// Wall-clock based like the page's own tick and PomoWatcher above — always
// recomputed from the server's endAt rather than decremented locally, so a
// throttled/backgrounded tab can't drift; it just shows the right number the
// next time it ticks or the tab regains focus. Paused timers fall back to
// the plain route title (see PomoWatcher for why pause needs its own check
// — same reasoning applies here).
function TitleManager() {
  const location = useLocation();
  const onSb = location.pathname === '/sb';
  const [pomo, setPomo] = useState(null);   // { endAt } while actively running, else null
  const tickRef = useRef(null);

  const fetchPomo = useCallback(async () => {
    if (!onSb) { setPomo(null); return; }
    const token = localStorage.getItem('token');
    if (!token) { setPomo(null); return; }
    try {
      const r = await fetch('/api/pomo/timer', { headers: { Authorization: 'Bearer ' + token } });
      if (!r.ok) { setPomo(null); return; }
      const t = await r.json();
      if (t && t.running && t.startTime && !t.pausedAt) {
        setPomo({ endAt: new Date(t.startTime).getTime() + t.duration * 60000 });
      } else {
        setPomo(null);
      }
    } catch { setPomo(null); }
  }, [onSb]);

  useEffect(() => {
    fetchPomo();
    document.addEventListener('visibilitychange', fetchPomo);
    window.addEventListener('pomo-timer-changed', fetchPomo);
    return () => {
      document.removeEventListener('visibilitychange', fetchPomo);
      window.removeEventListener('pomo-timer-changed', fetchPomo);
    };
  }, [fetchPomo]);

  useEffect(() => {
    const routeTitle = PAGE_TITLES[location.pathname] || 'Carolinge Li';
    clearInterval(tickRef.current);
    if (!onSb || !pomo) {
      document.title = routeTitle;
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.round((pomo.endAt - Date.now()) / 1000));
      const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
      const ss = String(remaining % 60).padStart(2, '0');
      document.title = `${mm}:${ss} · ${routeTitle}`;
    };
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickRef.current);
  }, [location.pathname, pomo, onSb]);

  return null;
}

// ── Pomodoro cross-page notification ──────────────────────────
// The watcher runs everywhere so the end-of-pomodoro chime + OS
// notification + in-app toast fire no matter which page you're on.
// Dedup is via localStorage keyed on the timer's startTime, so we never
// double-fire across the watcher tick, a Pomodoro-page tick, or a reload.
function firePomoEnd(t) {
  const key = t.startTime;
  if (localStorage.getItem('pomo-last-fired') === key) return;
  localStorage.setItem('pomo-last-fired', key);
  let settings = {};
  try { settings = JSON.parse(localStorage.getItem('pomo-settings') || '{}'); } catch {}
  if (settings.alertSounds !== false) { try { playEndChime(); } catch {} }
  if ('Notification' in window && Notification.permission === 'granted') {
    // Fire-and-forget — the SW-based path is async, but we don't need to
    // wait for it. `notificationclick` (in sw.js) handles focusing the tab.
    showOSNotification('🍅 番茄结束 · 休息一下', {
      body: `${t.projectTitle || ''}${t.taskTitle ? ' · ' + t.taskTitle : ''}`.trim() || '一个番茄钟结束了',
      tag: 'pomo-end-' + key,
      requireInteraction: true,
      data: { url: '/sb' },
    }).catch(() => {});
  }
  window.dispatchEvent(new CustomEvent('pomo-ended', { detail: t }));
}

function PomoWatcher() {
  const endTimeoutRef = useRef(null);

  const check = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { clearTimeout(endTimeoutRef.current); return; }
    try {
      const r = await fetch('/api/pomo/timer', { headers: { Authorization: 'Bearer ' + token } });
      if (!r.ok) return;
      const t = await r.json();
      clearTimeout(endTimeoutRef.current);
      // Paused timers don't get a scheduled fire — pause doesn't move
      // startTime, so without this check the notification would still land
      // at the original (now wrong) end time regardless of how long the
      // pause lasts. Resume shifts startTime forward and re-fires this
      // check via the pomo-timer-changed event, rescheduling correctly.
      if (!t || !t.running || !t.startTime || t.pausedAt) return;
      const endAt = new Date(t.startTime).getTime() + t.duration * 60000;
      const remaining = endAt - Date.now();
      if (remaining <= 0) {
        firePomoEnd(t);
      } else {
        endTimeoutRef.current = setTimeout(() => firePomoEnd(t), remaining);
      }
    } catch {}
  }, []);

  useEffect(() => {
    check();
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pomo-timer-changed', check);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pomo-timer-changed', check);
      clearTimeout(endTimeoutRef.current);
    };
  }, [check]);

  return null;
}

// Auto-hide navbar on the 5 /t sub-pages — reveal when the mouse is
// within ~12px of the top edge (YouTube/Miro-style). Disabled on
// touch-primary devices (no hover). On entering an immersive route
// the nav is shown briefly (~1.8s) so it's discoverable.
const IMMERSIVE_ROUTES = ['/sb', '/sticker', '/ha', '/cook', '/todo'];

function ImmersiveNav() {
  const location = useLocation();
  const isImmersive = IMMERSIVE_ROUTES.includes(location.pathname);

  useEffect(() => {
    const supportsHover = window.matchMedia('(hover: hover)').matches;
    if (!isImmersive || !supportsHover) {
      document.body.classList.remove('immersive', 'nav-shown');
      return;
    }
    document.body.classList.add('immersive', 'nav-shown');
    const hideTimer = setTimeout(() => {
      document.body.classList.remove('nav-shown');
    }, 1800);
    const onMove = (e) => {
      if (e.clientY < 12) document.body.classList.add('nav-shown');
      else if (e.clientY > 80) document.body.classList.remove('nav-shown');
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      clearTimeout(hideTimer);
      window.removeEventListener('mousemove', onMove);
      document.body.classList.remove('immersive', 'nav-shown');
    };
  }, [isImmersive, location.pathname]);

  return null;
}

function PomoToast() {
  const [ended, setEnded] = useState(null);
  const timeoutRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      setEnded(e.detail || {});
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setEnded(null), 20000);
    };
    window.addEventListener('pomo-ended', handler);
    return () => {
      window.removeEventListener('pomo-ended', handler);
      clearTimeout(timeoutRef.current);
    };
  }, []);
  if (!ended) return null;
  const subtitle = `${ended.projectTitle || ''}${ended.taskTitle ? ' · ' + ended.taskTitle : ''}`.trim();
  return (
    <div className="pomo-toast" role="alert">
      <span className="pomo-toast-emoji">🍅</span>
      <div className="pomo-toast-body">
        <div className="pomo-toast-title">番茄结束</div>
        {subtitle && <div className="pomo-toast-sub">{subtitle}</div>}
      </div>
      <Link to="/sb" className="pomo-toast-link" onClick={() => setEnded(null)}>去看看 →</Link>
      <button className="pomo-toast-close" onClick={() => setEnded(null)} aria-label="dismiss">×</button>
    </div>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [showGlow, setShowGlow] = useState(false);
  const [theme, setTheme] = useState(getDefaultTheme);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsLoggedIn(true);
    }
  }, []);

  // Register the notification service worker (macOS Chrome needs this to
  // actually surface notifications in the native Notification Center).
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  // Apply theme to <html> and persist
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  // Mouse glow effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
      setShowGlow(true);
    };

    const handleMouseLeave = () => {
      setShowGlow(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsLoggedIn(false);
  };

  return (
    <Router>
      <TitleManager />
      <ImmersiveNav />
      <PomoWatcher />
      <PomoToast />
      <div className="App">
        {/* Mouse glow effect */}
        <div 
          className="mouse-glow" 
          style={{
            left: `${mousePos.x}px`,
            top: `${mousePos.y}px`,
            opacity: showGlow ? 1 : 0
          }}
        ></div>

        <nav className="navbar">
          <div className="navbar-container">
            <Link to="/" className="navbar-brand">
              Carolinge Li
            </Link>
            <ul className="nav-menu">
              <li className="nav-item">
                <Link to="/" className="nav-link">Home</Link>
              </li>
              <li className="nav-item">
                <Link to="/cv" className="nav-link">CV</Link>
              </li>
              <li className="nav-item">
                <Link to="/research" className="nav-link">Research</Link>
              </li>
              <li className="nav-item">
                <Link to="/portfolio" className="nav-link">Portfolio</Link>
              </li>
              <li className="nav-item">
                <Link to="/blog" className="nav-link">Blog</Link>
              </li>
              <li className="nav-item">
                <button
                  className="theme-toggle"
                  onClick={toggleTheme}
                  title={theme === 'dark' ? '切换到浅色模式' : '切换到深色模式'}
                  aria-label="Toggle theme"
                >
                  {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                </button>
              </li>
              {isLoggedIn && (
                <li className="nav-item">
                  <button
                    className="nav-link logout-btn"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </li>
              )}
            </ul>
          </div>
        </nav>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/cv" element={<CV />} />
            <Route path="/research" element={<ResearchNew />} />
            {/* 旧的预览地址，别让人落到空白页 */}
            <Route path="/research-new" element={<Navigate to="/research" replace />} />
            {/* 私有：访客统计，刻意不放进导航 */}
            <Route path="/visits" element={<Visits />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/t" element={<Tools />} />
            <Route path="/sb" element={<Pomodoro />} />
            <Route path="/ha" element={<Habit />} />
            <Route path="/cook" element={<Recipe />} />
            <Route path="/todo" element={<Todo />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/sticker" element={<Sticker />} />
            <Route path="/table" element={<Table />} />
            <Route path="/sandbox" element={<Sandbox />} />
            <Route 
              path="/admin" 
              element={<Admin isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />} 
            />
          </Routes>
        </main>

        <footer className="footer">
          <p>&copy; 2026 Carolinge Li. All rights reserved.</p>
        </footer>
      </div>
    </Router>
  );
}

export default App;
